/**
 * SE-5492 — the card's "reports count + verdict" signal.
 * @remarks
 * The schema has NO scout-authored `verdict` field on `PlayerScoutReport`. The
 * shipped preview-drawer semantics (SE-5494,
 * {@link "@/features/player-kanban/utils/playerPreviewModel".selectLatestVerdict})
 * define the verdict as the MOST RECENT report's `overallScore`, labelled as
 * such so it is never mistaken for a distinct scout verdict. This module
 * applies the SAME rule on the board card so the two surfaces cannot disagree.
 *
 * Why this is a separate module rather than a call into `playerPreviewModel`:
 * that module's `buildReportRows` requires the full drawer selection set
 * (`reportId`, `scoutName`, formatted dates). The board's `ListKanbanCards`
 * read deliberately selects only `reportDate` + `overallScore` per report —
 * the two fields the verdict actually needs — to keep a 200-card board read
 * cheap. Sharing the formatting rule without sharing the selection set is the
 * point; `formatVerdictScore` below is the single rounding rule, and
 * `__tests__/cardReportSignal.test.ts` pins it against the drawer's behaviour.
 *
 * The COUNT prefers `Player.transferableDataCounts.scoutReportCount` — an
 * authoritative server-side total, not `scoutReports.length`. Those differ:
 * `scoutReports` is the viewer-visible report list, while the count is the
 * total attached to the player. The count is the honest "reports" number and
 * costs nothing extra to select.
 *
 * ## Why the count is no longer taken on faith (2026-08-07)
 *
 * It used to be `scoutReportCount ?? 0`, which turns "the server did not say"
 * into the assertion "there are none". Raniel caught the result on a pipeline
 * row that read **Reports 0 next to a Verdict chip of 2** — self-contradictory,
 * because the verdict IS the latest report's score, so a verdict existing is
 * proof a report exists. The drawer showed 1 for the same player: it reads the
 * same field from a DIFFERENT query (`PlayerDetailGetPlayer`) and, tellingly,
 * maps absence to `undefined` rather than to zero
 * ({@link "@/features/player-kanban/utils/playerPreviewModel".selectReportCount}).
 *
 * `ListKanbanCards` does select the field (see `operations.graphql`), so this
 * is the backend returning null-or-0 on the board path while returning 1 on the
 * detail path. **Which of those two it is was not observable from the client
 * without a live board read, and is flagged rather than guessed** — the rule
 * below is correct under either.
 *
 * The rule: the server's total wins whenever it is AT LEAST what we can already
 * see; otherwise the reports in hand win, because they are evidence and the
 * count is a claim. Absence is no longer evidence of zero. This makes the
 * contradiction structurally impossible — a non-null verdict implies a scored
 * report, which implies `visibleCount >= 1`, which implies `reportsCount >= 1`.
 * @module features/player-kanban/utils/cardReportSignal
 */

/** Minimal per-report shape the verdict derivation needs. */
export interface CardScoutReport {
  readonly reportDate?: string | null;
  readonly overallScore?: number | null;
}

/** The subset of a player the report signal is derived from. */
export interface ReportSignalSource {
  readonly transferableDataCounts?: {
    readonly scoutReportCount?: number | null;
  } | null;
  readonly scoutReports?: readonly (CardScoutReport | null)[] | null;
}

/** The derived signal rendered on the card. */
export interface CardReportSignal {
  /**
   * Total scout reports attached to the player — the server's count when it is
   * at least the number of reports in hand, otherwise that number. Never
   * contradicts {@link CardReportSignal.verdict}.
   */
  readonly reportsCount: number;
  /**
   * The most recent report's overall score, rounded to one decimal place, or
   * `null` when the player has no report carrying a score.
   */
  readonly verdict: string | null;
}

/**
 * Round a score to at most one decimal place.
 * @remarks
 * Mirrors `playerPreviewModel`'s private `formatScore` exactly so the card and
 * the drawer render the same verdict string for the same report.
 * @param value - The raw overall score.
 * @returns The formatted score, or `null` when absent or not a number.
 */
export const formatVerdictScore = (
  value: number | null | undefined
): string | null =>
  value == null || Number.isNaN(value)
    ? null
    : String(Math.round(value * 10) / 10);

/**
 * Parse a report date into a sortable epoch.
 * @remarks
 * An absent or unparseable date sorts LAST (negative infinity) rather than
 * throwing or being treated as "now" — a report with no date must never
 * out-rank a dated one and silently become the verdict.
 * @param reportDate - ISO timestamp, possibly absent or malformed.
 * @returns Epoch milliseconds, or `Number.NEGATIVE_INFINITY`.
 */
const toSortKey = (reportDate: string | null | undefined): number => {
  if (!reportDate) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(reportDate);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

/**
 * Derive the card's reports count and verdict.
 * @param player - The player's report-bearing fields from `ListKanbanCards`.
 * @returns The {@link CardReportSignal}.
 */
export const selectCardReportSignal = (
  player: ReportSignalSource | null | undefined
): CardReportSignal => {
  const present = (player?.scoutReports ?? []).filter(
    (report): report is CardScoutReport => report != null
  );

  // An unscored report is still a report, so the floor counts every row we
  // hold, not just the ones the verdict could have come from.
  const serverCount = player?.transferableDataCounts?.scoutReportCount;
  const reportsCount =
    serverCount != null && serverCount >= present.length
      ? serverCount
      : present.length;

  const scored = present.filter(
    report => formatVerdictScore(report.overallScore) !== null
  );

  if (scored.length === 0) return { reportsCount, verdict: null };

  // Single pass for the max — the list is small, but this avoids mutating the
  // caller's array (a `.sort()` on a frozen Apollo result would throw). The
  // explicit seed is the first scored entry, which the guard above proves
  // exists.
  const latest = scored.reduce(
    (best, candidate) =>
      toSortKey(candidate.reportDate) > toSortKey(best.reportDate)
        ? candidate
        : best,
    scored[0]
  );

  return { reportsCount, verdict: formatVerdictScore(latest.overallScore) };
};
